/**
 * Renders a single MCP tool call inline within an assistant turn.
 *
 * States: pending → ok | error. Shows name, server, args, and result
 * (or error). While Agent is pending, allowlisted TinyFish live
 * URLs render as a compact dock of mini screens; a finished site
 * drops out. Args + result are collapsible <details>.
 */
import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createEffect,
  createSignal,
} from "solid-js";

import {
  type BrowseWatch,
  isBrowseWatchUrl,
  toggleWatchExpanded,
} from "./browseWatch";
import type { ToolCallView } from "./turn";
import {
  describeSearchTiming,
  formatElapsed,
  parseSearchTiming,
} from "./turnPhase";

/**
 * Where a search call's time went, when the server reported the split.
 *
 * A single duration could not distinguish a slow search from slow page
 * reads — the distinction that matters, since page fetching is the variable
 * cost and the one that degrades to snippets.
 */
function timingLine(result: unknown): string | null {
  if (typeof result !== "object" || result === null) return null;
  const t = parseSearchTiming((result as { timing?: unknown }).timing);
  return t ? describeSearchTiming(t) : null;
}

interface ToolCallCardProps {
  call: ToolCallView;
}

const ToolCallCard: Component<ToolCallCardProps> = (props) => {
  return (
    <div class={`tool-call tool-call-${props.call.status}`}>
      <header class="tool-call-header">
        <span class="tool-call-name">
          <code>{props.call.name}</code>
        </span>
        <Show when={props.call.server}>
          <span class="tool-call-server muted">on {props.call.server}</span>
        </Show>
        <Switch>
          <Match when={props.call.status === "pending"}>
            <span class="tool-call-status">…calling</span>
          </Match>
          <Match when={props.call.status === "ok"}>
            <span class="tool-call-status status-ok">ok</span>
            <Show when={props.call.latency_ms !== undefined}>
              <span class="muted">
                {formatElapsed(props.call.latency_ms ?? 0)}
              </span>
            </Show>
          </Match>
          <Match when={props.call.status === "error"}>
            <span class="tool-call-status status-down">error</span>
            <Show when={props.call.latency_ms !== undefined}>
              <span class="muted">
                {formatElapsed(props.call.latency_ms ?? 0)}
              </span>
            </Show>
          </Match>
        </Switch>
      </header>
      <Show when={props.call.status === "pending" && (props.call.watch?.length ?? 0) > 0}>
        <WatchDock toolId={props.call.id} watches={props.call.watch ?? []} />
      </Show>
      <Show when={timingLine(props.call.result)}>
        {(line) => <div class="tool-call-timing muted">{line()}</div>}
      </Show>
      <Show when={props.call.args.length > 0}>
        <details class="tool-call-body">
          <summary>arguments</summary>
          <pre>{props.call.args}</pre>
        </details>
      </Show>
      <Show when={props.call.status === "error" && props.call.error}>
        <p class="chat-error">{props.call.error}</p>
      </Show>
      <Show
        when={props.call.status === "ok" && props.call.result !== undefined}
      >
        <details class="tool-call-body">
          <summary>result</summary>
          <pre>{formatResult(props.call.result)}</pre>
        </details>
      </Show>
    </div>
  );
};

// `paired()` yields new object refs on every Agent progress event, so
// `<For>` remounts TurnBlock/WatchDock. A component-local expanded
// signal would snap back to mini on each TinyFish step. Same pattern
// as `thinkingStartedAt` in ChatPage.
const watchExpandedByTool = new Map<string, string>();

const WatchDock: Component<{ toolId: string; watches: BrowseWatch[] }> = (
  props,
) => {
  const [expanded, setExpanded] = createSignal<string | null>(
    watchExpandedByTool.get(props.toolId) ?? null,
  );

  createEffect(() => {
    const hosts = new Set(props.watches.map((w) => w.host));
    const cur = expanded();
    if (cur && !hosts.has(cur)) {
      watchExpandedByTool.delete(props.toolId);
      setExpanded(null);
    }
  });

  const toggle = (host: string) => {
    setExpanded((cur) => {
      const next = toggleWatchExpanded(cur, host);
      if (next) watchExpandedByTool.set(props.toolId, next);
      else watchExpandedByTool.delete(props.toolId);
      return next;
    });
  };

  return (
    <div class="watch-dock">
      <For each={props.watches}>
        {(w) => {
          const live = Boolean(w.url && isBrowseWatchUrl(w.url));
          return (
            <figure
              class="watch-tile"
              classList={{
                "is-expanded": expanded() === w.host,
                "is-queued": !live,
              }}
            >
              <div class="watch-tile-screen">
                <Show when={live}>
                  <iframe
                    src={w.url}
                    title={`Live browser · ${w.host}`}
                    sandbox="allow-scripts allow-same-origin"
                    referrerpolicy="no-referrer"
                    tabIndex={-1}
                  />
                </Show>
                <Show when={live}>
                  <button
                    type="button"
                    class="watch-tile-hit"
                    aria-expanded={expanded() === w.host}
                    aria-label={
                      expanded() === w.host
                        ? `Shrink live view of ${w.host}`
                        : `Enlarge live view of ${w.host}`
                    }
                    onClick={() => toggle(w.host)}
                  />
                </Show>
              </div>
              <figcaption class="watch-tile-cap muted">
                <span class="watch-tile-host">{w.host || "live"}</span>
                <Show when={live} fallback={<span>queued</span>}>
                  <a href={w.url} target="_blank" rel="noopener noreferrer">
                    open
                  </a>
                </Show>
              </figcaption>
            </figure>
          );
        }}
      </For>
    </div>
  );
};

function formatResult(value: unknown): string {
  if (value === null || value === undefined) return "(empty)";
  // Search results are wrapped as `{timing, sources}` so the header can show
  // the split; the dropdown should still show the sources themselves rather
  // than the envelope.
  if (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { sources?: unknown }).sources)
  ) {
    return formatResult((value as { sources: unknown }).sources);
  }
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default ToolCallCard;
